import React, { useEffect, useRef } from 'react';

/**
 * SlideOver
 * Overlay panel that slides in from the right over the page content.
 * - Does not move the page layout
 * - Supports animated open/close with backdrop
 * - Width presets and focus mode (80vw)
 */
const widthToPx = (width) => {
  switch (width) {
    case 'md':
      return 520;
    case 'lg':
      return 640;
    case 'xl':
      return 760;
    case 'focus':
      return null; // handled by vw
    default:
      return 640;
  }
};

const SlideOver = ({
  isOpen,
  onClose,
  onExited,
  width = 'lg',
  title,
  footer,
  children,
  closing = false,
}) => {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);

  // ESC to close and prevent body scroll
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    };
    
    if (isOpen) {
      // Prevent body scroll when sidebar is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      window.addEventListener('keydown', handler);
    } else {
      // Restore body scroll when sidebar is closed
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    }
    
    return () => {
      window.removeEventListener('keydown', handler);
      
      // Ensure body scroll is restored on cleanup
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose && onClose();
    }
  };

  const computedWidth = width === 'focus' ? '80vw' : `${widthToPx(width)}px`;

  return (
    <>
      {isOpen && (
        <div
          ref={overlayRef}
          className={`slideover-overlay ${closing ? 'closing' : ''}`}
          role="dialog"
          aria-modal="true"
          onClick={handleOverlayClick}
          onTransitionEnd={(e) => {
            if (e.target === overlayRef.current && closing) {
              onExited && onExited();
            }
          }}
        >
          <div
            ref={panelRef}
            className={`slideover-panel ${closing ? 'closing' : ''}`}
            style={{ width: computedWidth }}
          >
            {title && (
              <div className="slideover-header">
                <h3 className="slideover-title">{title}</h3>
                <button className="slideover-close" aria-label="Close" onClick={onClose}>
                  ×
                </button>
              </div>
            )}
            <div className="slideover-body">{children}</div>
            {footer && <div className="slideover-footer">{footer}</div>}
            {/* Mobile: Prominent close button at bottom */}
            <div className="slideover-mobile-close-button">
              <button onClick={onClose} aria-label="Close">
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .slideover-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.35);
          z-index: 1100;
          display: flex;
          justify-content: flex-end;
          opacity: 1;
          transition: opacity 200ms ease;
          overscroll-behavior: none; /* Prevent scroll chaining to main page */
        }
        .slideover-overlay.closing {
          opacity: 0;
        }
        .slideover-panel {
          height: 100vh;
          background: var(--bg-primary);
          border-left: 1px solid var(--border-light);
          box-shadow: -4px 0 24px rgba(0,0,0,0.15);
          transform: translateX(0);
          transition: transform 220ms ease;
          display: flex;
          flex-direction: column;
          overscroll-behavior: contain; /* Contain scrolling within panel */
          touch-action: auto; /* Allow normal touch scrolling within panel */
        }
        .slideover-panel.closing {
          transform: translateX(100%);
        }
        .slideover-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-light);
          position: relative;
        }
        .slideover-title { margin: 0; font-size: 1.1rem; }
        .slideover-close {
          background: none; border: none; font-size: 1.5rem; cursor: pointer;
          color: var(--text-secondary);
          min-width: 44px;
          min-height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s ease;
        }
        .slideover-close:hover { 
          color: var(--text-primary);
          background: var(--bg-hover);
        }
        .slideover-close:active {
          transform: scale(0.95);
        }
        .slideover-body { 
          padding: 20px; 
          overflow-y: auto; 
          overflow-x: hidden;
          flex: 1; 
          height: 0; /* Forces flex item to respect overflow */
          overscroll-behavior: contain; /* Prevents scroll chaining */
        }
        
        /* Ensure form elements don't interfere with scrolling */
        .slideover-body * {
          overscroll-behavior: auto; /* Allow normal scrolling for all elements */
        }
        
        /* Allow scrolling on form elements */
        .slideover-body form,
        .slideover-body .form-group,
        .slideover-body input,
        .slideover-body select,
        .slideover-body textarea,
        .slideover-body .card,
        .slideover-body .card-body,
        .slideover-body .card-actions,
        .slideover-body .tenant-settings-panel,
        .slideover-body .panel-header,
        .slideover-body .switch,
        .slideover-body .slider {
          overscroll-behavior: auto; /* Allow scrolling on all interactive elements */
          touch-action: auto; /* Allow touch scrolling */
        }
        
        /* Ensure no element blocks scrolling */
        .slideover-body * {
          pointer-events: auto; /* Ensure all elements are interactive */
        }
        .slideover-footer { padding: 12px 20px; border-top: 1px solid var(--border-light); }
        
        /* Mobile close button - hidden on desktop */
        .slideover-mobile-close-button {
          display: none;
        }
        
        @media (max-width: 768px) {
          .slideover-panel { width: 100vw !important; }
          
          .slideover-header {
            padding: 16px;
            min-height: 56px;
          }
          
          .slideover-title { 
            font-size: 1rem;
            font-weight: 600;
          }
          
          .slideover-close {
            font-size: 1.75rem;
            min-width: 48px;
            min-height: 48px;
            color: var(--text-primary);
            background: var(--bg-secondary);
            border: 1px solid var(--border-color);
          }
          
          .slideover-close:active {
            background: var(--bg-hover);
            transform: scale(0.9);
          }
          
          /* Add prominent close button at bottom on mobile */
          .slideover-body {
            padding-bottom: 80px;
          }
          
          .slideover-mobile-close-button {
            display: block;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            padding: 16px;
            background: var(--bg-primary);
            border-top: 1px solid var(--border-color);
            z-index: 1101;
            box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
          }
          
          .slideover-mobile-close-button button {
            width: 100%;
            min-height: 48px;
            padding: 12px 24px;
            background: var(--bg-secondary);
            border: 1px solid var(--border-color);
            border-radius: 8px;
            color: var(--text-primary);
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }
          
          .slideover-mobile-close-button button:active {
            background: var(--bg-hover);
            transform: scale(0.98);
          }
        }
      `}</style>
    </>
  );
};

export default SlideOver;


